if (!customElements.get('skn-mobile-carousel')) {
  customElements.define('skn-mobile-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.cards = [...this.querySelectorAll('[data-carousel-card]')];
      this.dots = [...this.querySelectorAll('[data-card-select]')];
      this.toggle = this.querySelector('[data-carousel-toggle]');
      if (!this.track || this.cards.length < 2 || !this.toggle) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const passive = { passive: true, signal };
      this.mobile = matchMedia(`(max-width: ${Number(this.dataset.mobileMax) || 749}px)`);
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = false;
      this.visible = false;
      this.pressing = false;
      this.hovered = false;
      this.section = this.closest('skn-home-lineup, skn-shop-all, skn-resource-story');
      this.refresh = () => {
        this.measure();
        this.toggle.hidden = this.motion.matches;
        this.sync();
        this.schedule();
      };
      this.track.addEventListener('scroll', () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.sync();
        });
        this.schedule();
      }, passive);
      this.addEventListener('pointerdown', () => {
        this.pressing = true;
        this.stop();
      }, passive);
      const release = () => {
        this.pressing = false;
        this.schedule();
      };
      window.addEventListener('pointerup', release, passive);
      window.addEventListener('pointercancel', release, passive);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.stop();
      }, passive);
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.schedule();
      }, passive);
      this.section?.addEventListener('focusin', () => this.stop(), { signal });
      this.section?.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), { signal });
      this.addEventListener('click', event => {
        const dot = event.target.closest('[data-card-select]');
        if (dot && this.contains(dot)) {
          event.stopPropagation();
          this.goTo(this.dots.indexOf(dot));
        }
        if (event.target.closest('[data-carousel-toggle]') === this.toggle) {
          this.paused = !this.paused;
          this.toggle.setAttribute('aria-label', this.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
          this.toggle.setAttribute('aria-pressed', String(this.paused));
        }
        this.schedule();
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        this.goTo(this.order[(this.order.indexOf(this.active) + direction + this.cards.length) % this.cards.length]);
      }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.mobile.addEventListener('change', this.refresh, { signal });
      this.motion.addEventListener('change', this.refresh, { signal });
      window.addEventListener('resize', this.refresh, passive);
      this.stateObserver = new MutationObserver(() => this.schedule());
      this.cards.forEach(card => this.stateObserver.observe(card, { attributes: true, attributeFilter: ['data-open', 'class'] }));
      this.section?.querySelectorAll('dialog').forEach(dialog => this.stateObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] }));
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .2);
          this.schedule();
        }, { threshold: [0, .2] });
        this.observer.observe(this.track);
      } else {
        this.visible = true;
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.refresh);
        this.resizeObserver.observe(this.track);
      }
      this.refresh();
      this.setAttribute('data-carousel-ready', '');
    }
    measure() {
      const padding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.targets = this.cards.map(card => {
        const center = getComputedStyle(card).scrollSnapAlign === 'center';
        return Math.max(0, Math.min(max, card.offsetLeft - (center ? (this.track.clientWidth - card.offsetWidth) / 2 : padding)));
      });
      this.order = this.cards.map((card, index) => index).sort((a, b) => this.cards[a].offsetLeft - this.cards[b].offsetLeft);
      this.dots.forEach((dot, index) => { dot.style.order = this.order.indexOf(index); });
    }
    sync() {
      this.active = this.order.reduce((best, index) => Math.abs(this.targets[index] - this.track.scrollLeft) < Math.abs(this.targets[best] - this.track.scrollLeft) ? index : best, this.order[0]);
      this.dots.forEach((dot, index) => {
        if (index === this.active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
    canPlay() {
      const focused = this.section?.contains(document.activeElement) && document.activeElement !== this.toggle;
      return this.isConnected && this.mobile.matches && !this.motion.matches && this.visible && !document.hidden && !this.paused && !this.pressing && !this.hovered && !focused && !this.section?.querySelector('dialog[open], [data-carousel-card][data-open], [data-carousel-card].is-revealed') && this.track.scrollWidth > this.track.clientWidth + 1;
    }
    stop() {
      clearTimeout(this.timer);
      this.timer = 0;
    }
    schedule() {
      this.stop();
      if (!this.canPlay()) return;
      this.timer = setTimeout(() => {
        if (!this.canPlay()) return;
        this.sync();
        this.goTo(this.order[(this.order.indexOf(this.active) + 1) % this.cards.length]);
      }, 5000);
    }
    goTo(index) {
      if (!Number.isInteger(index) || !this.cards[index]) return;
      this.track.scrollTo({ left: this.targets[index], behavior: this.motion.matches ? 'instant' : 'smooth' });
      this.schedule();
    }
    disconnectedCallback() {
      this.stop();
      cancelAnimationFrame(this.frame);
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.stateObserver?.disconnect();
      this.removeAttribute('data-carousel-ready');
    }
  });
}
